const MOVIE_API_KEY = 'f4f420415a3acfc2912b2cf0e2e19298';

// Helper to fetch and handle JSON response
const fetchJSON = async (url) => {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`API request failed: ${response.status}`);
  }
  return response.json();
};

// Search for a specific actor
export const searchActorInTMDB = async (name) => {
  try {
    console.log('Searching for actor:', name);
    const data = await fetchJSON(
      `https://api.themoviedb.org/3/search/person?api_key=${MOVIE_API_KEY}&query=${encodeURIComponent(name)}`
    );
    
    if (data.results?.length > 0) {
      // Sort by popularity to get the most likely match
      const actors = data.results
        .filter(person => person.known_for_department === "Acting")
        .sort((a, b) => b.popularity - a.popularity);

      if (actors.length > 0) {
        console.log('Found actor:', actors[0].name);
        return actors[0].name;
      }
    }
    return null;
  } catch (error) {
    console.error('Search actor error:', error);
    return null;
  }
};

// Search for a director
export const searchDirector = async (name) => {
  try {
    const data = await fetchJSON(
      `https://api.themoviedb.org/3/search/person?api_key=${MOVIE_API_KEY}&query=${encodeURIComponent(name)}`
    );
    
    if (data.results?.length > 0) {
      const possibleDirector = data.results[0];
      if (possibleDirector.known_for_department === "Directing") {
        return possibleDirector.name;
      }
    }
    return null;
  } catch (error) {
    console.error('Search director error:', error);
    return null;
  }
};

// Get movie credits to find the director
export const getMovieCredits = async (movieId) => {
  try {
    const data = await fetchJSON(
      `https://api.themoviedb.org/3/movie/${movieId}/credits?api_key=${MOVIE_API_KEY}`
    );
    const directors = data.crew.filter(person => person.job === "Director");
    return directors.map(director => director.name).join(', ');
  } catch (error) {
    console.error('Error fetching director:', error);
    return '';
  }
};

// Get all movie genres
export const getGenres = async () => {
  try {
    const data = await fetchJSON(
      `https://api.themoviedb.org/3/genre/movie/list?api_key=${MOVIE_API_KEY}`
    );
    return data.genres || [];
  } catch (error) {
    console.error('Error fetching genres:', error);
    return [];
  }
};

// Search for movies by title
export const searchByTitle = async (titleQuery) => {
  try {
    if (!titleQuery) {
      return { message: 'Please provide a movie title to search for.', results: [] };
    }

    const data = await fetchJSON(
      `https://api.themoviedb.org/3/search/movie?api_key=${MOVIE_API_KEY}&query=${encodeURIComponent(titleQuery)}&page=1`
    );
    let results = data.results.filter(movie => 
      movie.poster_path && 
      movie.title.toLowerCase().includes(titleQuery.toLowerCase())
    );

    if (results.length === 0) {
      return { message: `No movies found with title "${titleQuery}"`, results: [] };
    }

    const genres = await getGenres();
    results = addGenreNames(results, genres);
    
    // Add director info
    const resultsWithDirectors = await Promise.all(results.map(async (movie) => {
      const director = await getMovieCredits(movie.id);
      return { ...movie, director };
    }));

    return { message: null, results: resultsWithDirectors };
  } catch (error) {
    console.error('Title search error:', error);
    return { message: 'Error searching for movies by title', results: [] };
  }
};

// Search for movies by actor
export const searchByActor = async (actorName) => {
  try {
    // Search for the actor
    const actorResponse = await fetchJSON(
      `https://api.themoviedb.org/3/search/person?api_key=${MOVIE_API_KEY}&query=${encodeURIComponent(actorName)}`
    );

    if (!actorResponse.results?.length) {
      return { message: `No actor found with name "${actorName}"`, results: [] };
    }

    const actor = actorResponse.results[0];
    let results = [];
    const pagesToFetch = 20;  // Fetch 20 pages for more results
    
    // Fetch by popularity
    for (let page = 1; page <= pagesToFetch; page++) {
      const moviesData = await fetchJSON(
        `https://api.themoviedb.org/3/discover/movie?api_key=${MOVIE_API_KEY}&` +
        `with_cast=${actor.id}&sort_by=popularity.desc&page=${page}&` +
        `vote_count.gte=100` // Reduced threshold for more results
      );
      if (moviesData.results) {
        results.push(...moviesData.results);
      }
    }
    
    // Fetch recent movies
    for (let page = 1; page <= 5; page++) {
      const recentMoviesData = await fetchJSON(
        `https://api.themoviedb.org/3/discover/movie?api_key=${MOVIE_API_KEY}&` +
        `with_cast=${actor.id}&sort_by=release_date.desc&page=${page}&` +
        `vote_count.gte=50`
      );
      if (recentMoviesData.results) {
        results.push(...recentMoviesData.results);
      }
    }

    // Remove duplicates
    results = results.filter((movie, index, self) =>
      index === self.findIndex((m) => m.id === movie.id)
    );

    // Filter valid results
    results = results.filter(movie => movie.poster_path);
    
    if (results.length === 0) {
      return { 
        message: `Found actor ${actor.name}, but couldn't find any movies. Try another actor.`,
        results: []
      };
    }
    
    // Add genre names
    const genres = await getGenres();
    results = addGenreNames(results, genres);
    
    return {
      message: `Found ${results.length} movies starring ${actor.name}. Showing diverse selection of their work.`,
      results: results,
      actorName: actor.name
    };
  } catch (error) {
    console.error('Actor search error:', error);
    return { message: 'Error searching for movies by actor', results: [] };
  }
};

// Search for movies by director
export const searchByDirector = async (directorName) => {
  try {
    const directorResponse = await fetchJSON(
      `https://api.themoviedb.org/3/search/person?api_key=${MOVIE_API_KEY}&query=${encodeURIComponent(directorName)}`
    );

    if (!directorResponse.results?.length) {
      return { message: `No director found with name "${directorName}"`, results: [] };
    }

    const director = directorResponse.results[0];
    let results = [];
    const pagesToFetch = 20;
    
    // Get popular movies
    for (let page = 1; page <= pagesToFetch; page++) {
      const moviesData = await fetchJSON(
        `https://api.themoviedb.org/3/discover/movie?api_key=${MOVIE_API_KEY}&` +
        `with_crew=${director.id}&sort_by=popularity.desc&page=${page}&` +
        `vote_count.gte=50`
      );
      if (moviesData.results) {
        results.push(...moviesData.results);
      }
    }
    
    // Get recent movies
    for (let page = 1; page <= 5; page++) {
      const recentMoviesData = await fetchJSON(
        `https://api.themoviedb.org/3/discover/movie?api_key=${MOVIE_API_KEY}&` +
        `with_crew=${director.id}&sort_by=release_date.desc&page=${page}`
      );
      if (recentMoviesData.results) {
        results.push(...recentMoviesData.results);
      }
    }

    // Filter and remove duplicates
    results = results
      .filter(movie => movie.poster_path)
      .filter((movie, index, self) =>
        index === self.findIndex((m) => m.id === movie.id)
      );
    
    if (results.length === 0) {
      return { 
        message: `Found director ${director.name}, but couldn't find any movies. Try another director.`,
        results: []
      };
    }

    // Add genre names
    const genres = await getGenres();
    results = addGenreNames(results, genres);
    
    // Add director to each movie
    results = results.map(movie => ({
      ...movie,
      director: director.name
    }));

    return {
      message: `Found ${results.length} movies directed by ${director.name}.`,
      results: results
    };
  } catch (error) {
    console.error('Director search error:', error);
    return { message: 'Error searching for movies by director', results: [] };
  }
};

// Search by year or era/timeframe
export const searchByTimeframe = async (timeframe) => {
  try {
    const baseUrl = `https://api.themoviedb.org/3/discover/movie?api_key=${MOVIE_API_KEY}`;
    let dateParams = '';

    switch (timeframe.type) {
      case 'year':
        dateParams = `&primary_release_year=${timeframe.value}`;
        break;
      case 'decade':
      case 'era':
        dateParams = `&primary_release_date.gte=${timeframe.start}-01-01&primary_release_date.lte=${timeframe.end}-12-31`;
        break;
      default:
        break;
    }

    let allResults = [];
    const pagesToFetch = 15;

    for (let page = 1; page <= pagesToFetch; page++) {
      const data = await fetchJSON(
        `${baseUrl}${dateParams}&sort_by=vote_average.desc&page=${page}&` +
        `vote_count.gte=100` // Reduced threshold
      );
      if (data.results) {
        allResults.push(...data.results);
      }
    }

    if (allResults.length === 0) {
      return { message: 'No movies found from that time period.', results: [] };
    }

    // Filter valid results
    allResults = allResults.filter(movie => {
      if (!movie.release_date) return false;
      const year = parseInt(movie.release_date.split('-')[0]);
      return movie.poster_path && 
             movie.vote_count >= 100 && 
             year >= parseInt(timeframe.start) && 
             year <= parseInt(timeframe.end);
    });

    // Add genre names
    const genres = await getGenres();
    allResults = addGenreNames(allResults, genres);
    
    const timeDescription = timeframe.type === 'year' 
      ? timeframe.value 
      : `${timeframe.start}-${timeframe.end}`;
    
    return {
      message: `Found ${allResults.length} movies from ${timeDescription}.`,
      results: allResults
    };
  } catch (error) {
    console.error('Timeframe search error:', error);
    return { message: 'Error searching by timeframe', results: [] };
  }
};

// Search by genre/keywords
export const searchByKeywords = async (searchTerms) => {
  try {
    const cleanedTerms = searchTerms.toLowerCase().trim();
    
    // Genre mapping (simplified version)
    const simpleGenreMap = {
      'romantic': 10749,
      'romance': 10749,
      'action': 28,
      'comedy': 35,
      'drama': 18,
      'horror': 27,
      'thriller': 53,
      'adventure': 12,
      'fantasy': 14,
      'sci-fi': 878,
      'science fiction': 878,
      'documentary': 99,
      'crime': 80,
      'mystery': 9648,
      'family': 10751,
      'animated': 16,
      'animation': 16,
      'anime': 16,
      'cartoon': 16,
      'musical': 10402,
      'music': 10402,
      'history': 36,
      'war': 10752,
      'western': 37,
      'superhero': [28, 14],
      'kids': [16, 10751],
      'children': [16, 10751]
    };

    // Extract genre terms
    const genreTerms = cleanedTerms.split(/[\s,]+/).filter(term => term.length > 2);
    let allGenreIds = [];
    
    // Find matching genres
    for (const term of genreTerms) {
      for (const [genreTerm, id] of Object.entries(simpleGenreMap)) {
        if (genreTerm.includes(term) || term.includes(genreTerm)) {
          if (Array.isArray(id)) {
            allGenreIds.push(...id);
          } else {
            allGenreIds.push(id);
          }
        }
      }
    }
    
    // Remove duplicates
    allGenreIds = [...new Set(allGenreIds)];
    
    if (allGenreIds.length === 0) {
      return { message: 'No specific genre found in your search terms.', results: [] };
    }

    // Fetch movies for these genres
    let allResults = [];
    const pagesToFetch = 20;

    for (let page = 1; page <= pagesToFetch; page++) {
      const data = await fetchJSON(
        `https://api.themoviedb.org/3/discover/movie?api_key=${MOVIE_API_KEY}&` +
        `with_genres=${allGenreIds.join(',')}&sort_by=popularity.desc&page=${page}&` +
        `vote_count.gte=100`
      );
      
      if (data.results?.length) {
        allResults.push(...data.results);
      }
    }
    
    // Filter and deduplicate
    allResults = allResults
      .filter(movie => movie.poster_path)
      .filter((movie, index, self) =>
        index === self.findIndex((m) => m.id === movie.id)
      );
    
    if (allResults.length === 0) {
      return { message: 'No movies found matching your criteria. Try different terms.', results: [] };
    }

    // Find matched genre terms
    const searchedGenres = new Set();
    const terms = cleanedTerms.toLowerCase().split(/[\s,]+/);
    for (const term of terms) {
      for (const genreTerm of Object.keys(simpleGenreMap)) {
        if (genreTerm.includes(term) || term.includes(genreTerm)) {
          searchedGenres.add(genreTerm);
        }
      }
    }

    // Get genres for each movie
    const genres = await getGenres();
    allResults = allResults.map(movie => {
      const movieGenres = movie.genre_ids
        .map(id => genres.find(g => g.id === id)?.name?.toLowerCase())
        .filter(name => name);
      
      // Check for matches
      const matchingGenres = movieGenres.filter(genre => 
        Array.from(searchedGenres).some(searchedGenre => 
          genre.includes(searchedGenre) || 
          searchedGenre.includes(genre)
        )
      );

      return {
        ...movie,
        genreMatchCount: matchingGenres.length,
        totalGenreCount: movieGenres.length,
        movieGenres: movieGenres,
        matchingGenres: matchingGenres,
        isExactGenreMatch: matchingGenres.length > 0 && movieGenres.length === 1
      };
    });

    // Sort results with priority for exact genre matches
    allResults.sort((a, b) => {
      // First: movies with exactly one genre that matches
      if (a.isExactGenreMatch !== b.isExactGenreMatch) {
        return a.isExactGenreMatch ? -1 : 1;
      }
      
      // Second: movies with only the requested genres
      const aIsExact = a.genreMatchCount > 0 && a.genreMatchCount === a.totalGenreCount;
      const bIsExact = b.genreMatchCount > 0 && b.genreMatchCount === b.totalGenreCount;
      
      if (aIsExact !== bIsExact) {
        return aIsExact ? -1 : 1;
      }
      
      // Third: fewer total genres
      if (a.totalGenreCount !== b.totalGenreCount) {
        return a.totalGenreCount - b.totalGenreCount;
      }
      
      // Fourth: more matching genres
      if (a.genreMatchCount !== b.genreMatchCount) {
        return b.genreMatchCount - a.genreMatchCount;
      }

      // Finally: popularity
      return b.popularity - a.popularity;
    });

    // Add genre names for display
    allResults = addGenreNames(allResults, genres);
    
    // Prepare feedback message
    const genreName = Array.from(searchedGenres).join(' and ');
    const totalCount = allResults.length;
    const exactMatches = allResults.filter(m => m.isExactGenreMatch).length;
    
    let message;
    if (exactMatches > 0) {
      message = `Found ${totalCount} movies matching: ${genreName}. Showing ${exactMatches} pure ${genreName} movies first, followed by movies with multiple genres.`;
    } else {
      message = `Found ${totalCount} movies matching genre: ${genreName}.`;
    }
    
    return { message, results: allResults };
  } catch (error) {
    console.error('Keyword search error:', error);
    return { message: 'Error searching by genre', results: [] };
  }
};

// Search for top rated movies
export const searchTopRated = async () => {
  try {
    let allResults = [];
    const pagesToFetch = 15;

    for (let page = 1; page <= pagesToFetch; page++) {
      const data = await fetchJSON(
        `https://api.themoviedb.org/3/discover/movie?api_key=${MOVIE_API_KEY}&` +
        `sort_by=vote_average.desc&page=${page}&` +
        `vote_count.gte=3000&` + // Minimum votes
        `vote_average.gte=7.5` // Minimum rating
      );
      if (data.results) {
        allResults.push(...data.results);
      }
    }

    // Filter and sort
    allResults = allResults
      .filter(movie => movie.poster_path)
      .sort((a, b) => b.vote_average - a.vote_average);

    // Add genre names
    const genres = await getGenres();
    allResults = addGenreNames(allResults, genres);
    
    return {
      message: `Found ${allResults.length} top-rated movies.`,
      results: allResults
    };
  } catch (error) {
    console.error('Top rated search error:', error);
    return { message: 'Error searching for top rated movies', results: [] };
  }
};

// Utility function to add genre names to movies
function addGenreNames(movies, genres) {
  return movies.map(movie => ({
    ...movie,
    genre_names: movie.genre_ids
      .map(id => genres.find(g => g.id === id)?.name)
      .filter(name => name)
      .join(', ')
  }));
}

// Add director information to movies
export const addDirectorInfo = async (movies) => {
  const moviesWithDirectors = await Promise.all(movies.map(async (movie) => {
    if (!movie.director) {
      const director = await getMovieCredits(movie.id);
      return {
        ...movie,
        director
      };
    }
    return movie;
  }));
  return moviesWithDirectors;
};
